import React, { useState, useEffect } from 'react';
import api from '../api/axios';
import { useAuth } from '../contexts/AuthContext';

const TODAY = new Date().toISOString().split('T')[0];
const DELIVERY_STATUS_COLORS = {
  pending:'bg-yellow-100 text-yellow-700',
  in_transit:'bg-blue-100 text-blue-700',
  delivered:'bg-green-100 text-green-700',
  returned:'bg-red-100 text-red-600'
};
const EMPTY = {
  courier_date: TODAY, courier_company_id:'', tracking_number:'', sender_employee_id:'',
  receiver_city:'', receiver_company:'', receiver_person:'', description:'',
  weight:'', charges:'', payment_type:'on_time', rider_name:'', rider_phone:'',
  delivery_status:'pending'
};

export default function ExternalCourier() {
  const { user } = useAuth();
  const [couriers, setCouriers] = useState([]);
  const [courierCompanies, setCourierCompanies] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editing, setEditing] = useState(null);
  const [filterDate, setFilterDate] = useState(TODAY);
  const [loading, setLoading] = useState(true);
  const [empSearch, setEmpSearch] = useState('');

  // Auto-filled from employee
  const [autoFilled, setAutoFilled] = useState({ department:'', unit:'', company:'' });

  const load = async () => {
    if (!user) return;
    setLoading(true);
    try { const r = await api.get(`/api/couriers?date=${filterDate}`); setCouriers(r.data); }
    catch (err) { console.error('couriers:', err); setCouriers([]); }
    try { const r = await api.get('/api/courier-companies/active'); setCourierCompanies(r.data); }
    catch (err) { console.error('courier-companies:', err); setCourierCompanies([]); }
    try { const r = await api.get('/api/employees/active'); setEmployees(r.data); }
    catch (err) { console.error('employees:', err); setEmployees([]); }
    setLoading(false);
  };

  useEffect(() => { if (user) load(); }, [user?.role, filterDate]);

  const handleEmployeeChange = (empId) => {
    setForm(f => ({ ...f, sender_employee_id: empId }));
    if (!empId) { setAutoFilled({ department:'', unit:'', company:'' }); return; }
    const emp = employees.find(e => e.id === parseInt(empId));
    if (emp) {
      setAutoFilled({
        department: emp.department || '',
        unit: emp.unit_name || '',
        company: emp.company_name || ''
      });
    }
  };

  const totalDispatched = couriers.length;
  const totalCharges = couriers.reduce((s, c) => s + Number(c.charges||0), 0);
  const pendingCount = couriers.filter(c => c.delivery_status === 'pending').length;

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...form,
        courier_company_id: form.courier_company_id ? Number(form.courier_company_id) : null,
        sender_employee_id: form.sender_employee_id ? Number(form.sender_employee_id) : null,
        weight: form.weight ? Number(form.weight) : null,
        charges: form.charges ? Number(form.charges) : null
      };
      if (editing) await api.put(`/api/couriers/${editing}`, payload);
      else await api.post('/api/couriers', payload);
      setShowForm(false); setForm(EMPTY); setEditing(null);
      setAutoFilled({ department:'', unit:'', company:'' });
      setEmpSearch('');
      load();
    } catch (err) { alert(err.response?.data?.error || 'Save failed'); }
  };

  const handleEdit = (c) => {
    setForm({
      courier_date: c.courier_date||TODAY, courier_company_id: c.courier_company_id||'',
      tracking_number: c.tracking_number||'',
      sender_employee_id: c.sender_employee_id||'',
      receiver_city: c.receiver_city||'', receiver_company: c.receiver_company||'',
      receiver_person: c.receiver_person||'', description: c.description||'',
      weight: c.weight||'', charges: c.charges||'',
      payment_type: c.payment_type||'on_time',
      rider_name: c.rider_name||'', rider_phone: c.rider_phone||'',
      delivery_status: c.delivery_status||'pending'
    });
    setAutoFilled({
      department: c.sender_department || '',
      unit: c.sender_unit_name || '',
      company: c.company_name || ''
    });
    setEditing(c.id); setShowForm(true);
  };

  const handleStatusUpdate = async (id, delivery_status) => {
    try { await api.put(`/api/couriers/${id}`, { delivery_status }); load(); }
    catch { alert('Update failed'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this courier entry?')) return;
    try { await api.delete(`/api/couriers/${id}`); load(); }
    catch { alert('Delete failed'); }
  };

  const filteredEmployees = employees.filter(e =>
    !empSearch || e.name.toLowerCase().includes(empSearch.toLowerCase()) ||
    (e.employee_code||'').toLowerCase().includes(empSearch.toLowerCase())
  );

  const exportCSV = () => {
    const headers = ['Date','Courier Co','Tracking','Sender','Dept','City','Receiver','Charges','Status'];
    const rows = couriers.map(c => [
      c.courier_date, c.courier_company_name||'', c.tracking_number||'',
      c.sender_employee_name||'', c.sender_department||'',
      c.receiver_city||'', c.receiver_person||'',
      c.charges||0, c.delivery_status
    ]);
    const csv = [headers, ...rows].map(r => r.join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob); const a = document.createElement('a');
    a.href = url; a.download = 'external_couriers.csv'; a.click();
  };

  return (
    <div className="space-y-5">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">External Couriers</h1>
          <p className="text-gray-500 text-sm">TCS / Leopards / Other services</p>
        </div>
        <div className="flex gap-2">
          <button onClick={exportCSV}
            className="border border-gray-300 text-gray-600 px-4 py-2 rounded-lg text-sm hover:bg-gray-50">
            📥 Export CSV
          </button>
          <button onClick={() => {
              setForm(EMPTY); setEditing(null);
              setAutoFilled({ department:'', unit:'', company:'' });
              setEmpSearch(''); setShowForm(true);
            }}
            className="bg-blue-700 hover:bg-blue-800 text-white px-4 py-2 rounded-lg text-sm font-medium">
            ➕ New Courier
          </button>
        </div>
      </div>

      {/* Stats bar */}
      <div className="grid grid-cols-3 gap-4">
        {[
          { label:'Total Dispatched', val:totalDispatched, color:'text-blue-600' },
          { label:'Total Charges', val:`Rs ${totalCharges.toLocaleString()}`, color:'text-red-600' },
          { label:'Pending', val:pendingCount, color:'text-yellow-600' }
        ].map(s => (
          <div key={s.label} className="bg-white rounded-xl border border-gray-100 p-4 shadow-sm text-center">
            <p className="text-sm text-gray-500">{s.label}</p>
            <p className={`text-2xl font-bold ${s.color}`}>{s.val}</p>
          </div>
        ))}
      </div>

      <div className="bg-white rounded-xl border border-gray-100 p-4 flex gap-3">
        <input type="date" value={filterDate} onChange={e => setFilterDate(e.target.value)}
          className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
      </div>

      {/* Form Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b flex justify-between items-center sticky top-0 bg-white">
              <h2 className="text-xl font-bold text-gray-800">{editing ? 'Edit Courier' : 'New Courier'}</h2>
              <button onClick={() => { setShowForm(false); setEditing(null); }}
                className="text-gray-400 hover:text-gray-600 text-2xl">×</button>
            </div>
            <form onSubmit={handleSubmit} className="p-6 grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Courier Date *</label>
                <input type="date" value={form.courier_date}
                  onChange={e => setForm({...form,courier_date:e.target.value})} required
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Courier Company</label>
                <select value={form.courier_company_id} onChange={e => setForm({...form,courier_company_id:e.target.value})}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
                  <option value="">— Select Courier Company —</option>
                  {courierCompanies.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Tracking Number</label>
                <input type="text" value={form.tracking_number}
                  onChange={e => setForm({...form,tracking_number:e.target.value})}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
              </div>
              <div className="sm:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">Sender Employee</label>
                <input type="text" placeholder="Search employee name or code..."
                  value={empSearch} onChange={e => setEmpSearch(e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm mb-1 focus:outline-none focus:ring-2 focus:ring-blue-500" />
                <select value={form.sender_employee_id}
                  onChange={e => handleEmployeeChange(e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
                  <option value="">— Select Sender Employee —</option>
                  {(empSearch ? filteredEmployees : filteredEmployees.slice(0, 500)).map(e => (
                    <option key={e.id} value={e.id}>
                      {e.name} {e.employee_code ? `(${e.employee_code})` : ''} — {e.department || 'No dept'}
                    </option>
                  ))}
                  {!empSearch && filteredEmployees.length > 500 && (
                    <option disabled>— Type above to search all {filteredEmployees.length} employees —</option>
                  )}
                </select>
                {!empSearch && filteredEmployees.length > 500 && (
                  <p className="text-xs text-amber-600 mt-1">⚠️ Showing first 500 of {filteredEmployees.length} — type above to search all</p>
                )}
                {(autoFilled.department || autoFilled.unit || autoFilled.company) && (
                  <div className="mt-2 bg-blue-50 rounded-lg p-2 text-xs text-blue-700 flex gap-3">
                    {autoFilled.department && <span>🏢 {autoFilled.department}</span>}
                    {autoFilled.unit && <span>🏭 {autoFilled.unit}</span>}
                    {autoFilled.company && <span>🏗️ {autoFilled.company}</span>}
                  </div>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Receiver City</label>
                <input type="text" value={form.receiver_city}
                  onChange={e => setForm({...form,receiver_city:e.target.value})}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Receiver Company</label>
                <input type="text" value={form.receiver_company}
                  onChange={e => setForm({...form,receiver_company:e.target.value})}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Receiver Person</label>
                <input type="text" value={form.receiver_person}
                  onChange={e => setForm({...form,receiver_person:e.target.value})}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Weight (kg)</label>
                <input type="number" step="0.1" value={form.weight}
                  onChange={e => setForm({...form,weight:e.target.value})}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Charges (Rs)</label>
                <input type="number" value={form.charges}
                  onChange={e => setForm({...form,charges:e.target.value})}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Payment Type</label>
                <select value={form.payment_type}
                  onChange={e => setForm({...form,payment_type:e.target.value})}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
                  <option value="on_time">On Time</option>
                  <option value="prepaid">Prepaid</option>
                  <option value="cod">COD</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Rider Name</label>
                <input type="text" value={form.rider_name}
                  onChange={e => setForm({...form,rider_name:e.target.value})}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Rider Phone</label>
                <input type="text" value={form.rider_phone}
                  onChange={e => setForm({...form,rider_phone:e.target.value})}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
              </div>
              <div className="sm:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <textarea value={form.description}
                  onChange={e => setForm({...form,description:e.target.value})} rows={2}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Delivery Status</label>
                <select value={form.delivery_status}
                  onChange={e => setForm({...form,delivery_status:e.target.value})}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
                  <option value="pending">Pending</option>
                  <option value="in_transit">In Transit</option>
                  <option value="delivered">Delivered</option>
                  <option value="returned">Returned</option>
                </select>
              </div>
              <div className="sm:col-span-2 flex gap-3 pt-2 border-t">
                <button type="submit"
                  className="bg-blue-700 hover:bg-blue-800 text-white px-6 py-2 rounded-lg font-medium">
                  {editing ? 'Update' : 'Add Courier'}
                </button>
                <button type="button" onClick={() => { setShowForm(false); setEditing(null); }}
                  className="border border-gray-300 px-6 py-2 rounded-lg hover:bg-gray-50">Cancel</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Couriers Table */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
        {loading ? (
          <div className="p-12 text-center text-gray-400">
            <div className="text-4xl animate-pulse mb-2">📦</div>
            <p>Loading...</p>
          </div>
        ) : couriers.length === 0 ? (
          <div className="p-12 text-center text-gray-400">
            <div className="text-5xl mb-3">📦</div>
            <p>No couriers for {filterDate}</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50">
                <tr>
                  {['Date','Courier Co','Tracking','Sender','Dept/Unit','Receiver','Charges','Status','Actions'].map(h => (
                    <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {couriers.map(c => (
                  <tr key={c.id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-4 py-3 text-gray-600">{c.courier_date}</td>
                    <td className="px-4 py-3 font-medium">{c.courier_company_name||'—'}</td>
                    <td className="px-4 py-3 font-mono text-gray-600 text-xs">{c.tracking_number||'—'}</td>
                    <td className="px-4 py-3 text-gray-800">{c.sender_employee_name||'—'}</td>
                    <td className="px-4 py-3 text-xs text-gray-500">
                      {c.sender_department && <div>{c.sender_department}</div>}
                      {c.sender_unit_name && <div>{c.sender_unit_name}</div>}
                    </td>
                    <td className="px-4 py-3 text-xs">
                      <div className="font-medium">{c.receiver_person||'—'}</div>
                      <div className="text-gray-400">{c.receiver_city||''}{c.receiver_company ? ' · '+c.receiver_company : ''}</div>
                    </td>
                    <td className="px-4 py-3 font-medium">Rs {Number(c.charges||0).toLocaleString()}</td>
                    <td className="px-4 py-3">
                      <select value={c.delivery_status}
                        onChange={e => handleStatusUpdate(c.id, e.target.value)}
                        className={`px-2 py-1 rounded-full text-xs font-medium border-0 cursor-pointer ${DELIVERY_STATUS_COLORS[c.delivery_status]||'bg-gray-100 text-gray-500'}`}>
                        <option value="pending">Pending</option>
                        <option value="in_transit">In Transit</option>
                        <option value="delivered">Delivered</option>
                        <option value="returned">Returned</option>
                      </select>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex gap-2">
                        <button onClick={() => handleEdit(c)} className="text-blue-600 hover:text-blue-800 text-xs">✏️</button>
                        <button onClick={() => handleDelete(c.id)} className="text-red-500 hover:text-red-700 text-xs">🗑️</button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
